import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { ChevronDownBold } from '@sorare/core/src/atoms/icons/ChevronDownBold';
import { Collapse } from '@sorare/core/src/atoms/layout/Collapse';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LabelM } from '@sorare/core/src/atoms/typography';
import { useToggle } from '@sorare/core/src/hooks/useToggle';
import { glossary } from '@sorare/core/src/lib/glossary';
import type { Color } from '@sorare/core/src/style/types';

import { ThresholdRewardCollapseItem } from './ThresholdRewardCollapseItem';
import type {
  ThresholdRewardsCollapse_taskCampaign,
  ThresholdRewardsCollapse_thresholdStreakTaskThreshold,
} from './__generated__/index.graphql';

const AnimatedChevronDownBold = styled(ChevronDownBold)`
  &.expanded {
    transform: scale(-1);
  }
  transition: 300ms transform;
`;

type Props = {
  color: Color;
  thresholds: ThresholdRewardsCollapse_thresholdStreakTaskThreshold[];
  currentThresholdIndex: number;
  scoreTaskCampaign: ThresholdRewardsCollapse_taskCampaign | null;
};
export const ThresholdRewardsCollapse = ({
  color,
  thresholds,
  currentThresholdIndex,
  scoreTaskCampaign,
}: Props) => {
  const [rewardCollapseExpanded, toggleRewardCollapseExpanded] =
    useToggle(false);

  return (
    <Vertical>
      <ButtonBase onClick={() => toggleRewardCollapseExpanded()}>
        <Horizontal>
          <AnimatedChevronDownBold
            className={classNames({ expanded: rewardCollapseExpanded })}
          />
          <LabelM bold>
            <FormattedMessage {...glossary.rewards} />
          </LabelM>
        </Horizontal>
      </ButtonBase>
      <Collapse open={rewardCollapseExpanded}>
        <Vertical>
          {thresholds.map((threshold, i) => {
            const thresholdCompleted = currentThresholdIndex > i;
            const thresholdLocked =
              !threshold.current && currentThresholdIndex < i;

            return (
              <ThresholdRewardCollapseItem
                key={i}
                color={color}
                currentStreakToDisplay={i + 1}
                threshold={threshold}
                completed={thresholdCompleted}
                locked={thresholdLocked}
                scoreTaskCampaign={scoreTaskCampaign}
              />
            );
          })}
        </Vertical>
      </Collapse>
      {!rewardCollapseExpanded && (
        <ThresholdRewardCollapseItem
          color={color}
          currentStreakToDisplay={currentThresholdIndex + 1}
          threshold={thresholds[currentThresholdIndex]}
          scoreTaskCampaign={scoreTaskCampaign}
        />
      )}
    </Vertical>
  );
};

ThresholdRewardsCollapse.fragments = {
  taskCampaign: gql`
    fragment ThresholdRewardsCollapse_taskCampaign on TaskCampaign {
      slug
      ...ThresholdRewardCollapseItem_taskCampaign
    }
    ${ThresholdRewardCollapseItem.fragments.taskCampaign}
  ` as TypedDocumentNode<ThresholdRewardsCollapse_taskCampaign>,
  thresholdStreakTaskThreshold: gql`
    fragment ThresholdRewardsCollapse_thresholdStreakTaskThreshold on ThresholdStreakTaskThreshold {
      ...ThresholdRewardCollapseItem_thresholdStreakTaskThreshold
    }
    ${ThresholdRewardCollapseItem.fragments.thresholdStreakTaskThreshold}
  ` as TypedDocumentNode<ThresholdRewardsCollapse_thresholdStreakTaskThreshold>,
};
